import { useRef, useState, useEffect, useContext } from "react";
import { Stack } from "@fluentui/react";
import { DismissRegular, SquareRegular, ShieldLockRegular, ArrowUpRightRegular } from "@fluentui/react-icons";

import ReactMarkdown from "react-markdown";
import remarkGfm from 'remark-gfm'
import rehypeRaw from "rehype-raw";

import styles from "./ChatWidget.module.css";
import Kust_icon from "../../assets/icon.svg";
import Kust_White from "../../assets/kust_white.svg";
import HelpIcon from "../../assets/icon.svg";
import Kust_Blue from "../../assets/kust_blue.svg";
import Kust_Black from "../../assets/kust_black.svg";
import Operator from "../../assets/operator.svg";


import {
    conversationApi,
    getUserInfo,
    fetchConversationApi,
    createTicketSession,
    getConversationMessages,
    getBotSetup,
    fetchConversationApiMultipart,
} from "../../api/widget.api";
import { Answer } from "./components/Answer/Answer";
import { QuestionInput } from "./components/QuestionInput";
import { SESSION_ID } from "../../services/temp-session.service";
import { useCookies } from "react-cookie";
import React from "react";
import EventContext from "../../EventContext";
import { SessionError } from "../../errors/session.exception";

import Layout from "./pages/layout/Layout";
import { SystemMessageProvider, useSystemMessage } from "../SystemMessage/SystemMessageContext";
import { Attachment, ChatMessage, ChatResponse, Citation, ConversationRequest, ConversationSaveRequest, Role, ToolMessageContent } from "../../api";
import { HelpProvider, useHelp } from '../HelpContext/helpContext';
import { ChatWidgetProps } from "../../models/chatWidgetProps";
import { Message } from "../../models/Message";
import { useCallback } from "react";

const HOST = import.meta.env.VITE_API_HOST;

const ChatWidget: React.FC<ChatWidgetProps> = ({ id, botSettings, type }) => {
    // const [token, setToken] = useState('');
    const { closeEvent, eventTriggered, triggerEvent } = useContext(EventContext);

    const { systemMessage, setSystemMessage } = useSystemMessage();
    const { helpMode, setHelpDisabled, setHelpShowed } = useHelp();



    // useEffect(() => {
    //     if (eventTriggered) {
    //         setIsHelpMode(true);
    //         startIntervalConversation();
    //     }
    // }, [eventTriggered]);

    useEffect(() => {
        if (helpMode && helpMode.help) {
            setIsHelpMode(true)
            loadConversation();
            startIntervalConversation();
        }
    }, [helpMode]);

    const lastQuestionRef = useRef<string>("");
    const chatMessageStreamEnd = useRef<HTMLDivElement | null>(null);
    const chatMessageStreamRef = useRef<HTMLDivElement | null>(null);
    const [isPageReady, setIsPageReady] = useState<boolean>(false);
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [isError, setError] = useState<boolean>(false);
    const [showLoadingMessage, setShowLoadingMessage] = useState<boolean>(false);
    const [activeCitation, setActiveCitation] = useState<[content: string, id: string, title: string, filepath: string, url: string, metadata: string]>();
    const [isCitationPanelOpen, setIsCitationPanelOpen] = useState<boolean>(false);
    const [answers, setAnswers] = useState<ChatMessage[]>([]);
    const abortFuncs = useRef([] as AbortController[]);
    const [isHelpMode, setIsHelpMode] = useState<boolean>(false);
    const [isSupportRole, setIsSupportRole] = useState<boolean>(false);
    const [isChatOpen, setIsChatOpen] = useState(false);
    const toggleChat = () => setIsChatOpen(!isChatOpen);
    const [webId, setWebId] = useState<string | undefined>(undefined);
    let currentToken = useRef<string | undefined>();
    const [questions, setQuestions] = useState<any[]>([]);
    const [mainColor, setMainColor] = useState<string | undefined>(undefined);
    const [allowIntroduce, setAllowIntroduce] = useState<boolean>(false);
    const [introduceText, setIntroduceText] = useState<string | undefined>(undefined);
    const [allowFaq, setAllowFaq] = useState<boolean>(true);
    const [attachments, setAttachments] = useState<File[]>([]);
    const fileInputRef = useRef<HTMLInputElement | null>(null);
    const rootRef = useRef<HTMLDivElement | null>(null);
    const [isDraggingOver, setIsDraggingOver] = useState<boolean>(false);
    const [isAssistantPending, setPendingGPTRepsonse] = useState<boolean>(false);
    const replyPollTimeoutRef = useRef<any>(null);
    const [conversationLimitReached, setConversationLimitReached] = useState<boolean>(false);
    const [limitMessage, setLimitMessage] = useState<string>("");
    const [redirectPending, setRedirectPending] = useState<{ url: string; message: string, animation_title: string } | null>(null);

    const fileUrl = (id?: string) => {
        if (!id) return "";
        const token = (currentToken.current && currentToken.current !== "")
            ? currentToken.current
            : (sessionStorage.getItem('accessToken') as string);
        return `${HOST}/api/files/${id}${token ? `?token=${encodeURIComponent(token)}` : ""}`;
    };

    const handle429Response = async (response: Response) => {
        try {
            const body = await response.clone().json();
            const code: string = body?.code ?? "";
            const message: string = body?.error ?? "";

            if (code === "MESSAGE_LIMIT") {
                const displayMsg = message || "You've reached the conversation limit. Please start a new session.";
                setConversationLimitReached(true);
                setLimitMessage(displayMsg);
                setAnswers(prev => [...prev, { role: "system", content: displayMsg }]);
            } else {
                const displayMsg = message || "Too many sessions today. Please try again tomorrow.";
                setAnswers(prev => [...prev, { role: "system", content: displayMsg }]);
            }
        } catch {
            setAnswers(prev => [...prev, { role: "system", content: "Request limit reached. Please try again later." }]);
        }
    };

    const dataUrlToFile = (dataUrl: string, filename = `pasted-${Date.now()}.png`): File | null => {
        try {
            const [meta, base64] = dataUrl.split(",");
            const mime = (meta.match(/data:(.*?);base64/)?.[1]) || "image/png";
            const bin = atob(base64);
            const arr = new Uint8Array(bin.length);
            for (let i = 0; i < bin.length; i++) arr[i] = bin.charCodeAt(i);
            return new File([arr], filename, { type: mime });
        } catch {
            return null;
        }
    };

    const urlToFile = async (url: string): Promise<File | null> => {
        try {
            const res = await fetch(url, { mode: "cors" });
            if (!res.ok) return null;
            const blob = await res.blob();
            const extGuess = blob.type.split("/")[1] || "png";
            return new File([blob], `pasted-${Date.now()}.${extGuess}`, { type: blob.type });
        } catch {
            return null;
        }
    };

    const extractFirstImgSrcFromHtml = (html: string): string | null => {
        try {
            const doc = new DOMParser().parseFromString(html, "text/html");
            const img = doc.querySelector("img");
            return img?.getAttribute("src") || null;
        } catch {
            return null;
        }
    };

    const applyPastedFiles = (files: File[]) => {
        if (!files.length) return;

        const onlyImages = files.filter(f => /^image\//.test(f.type) && f.size > 0);

        if (!onlyImages.length) return;

        if (!isSupportRole) {
            setAttachments([onlyImages[0]]);
        } else {
            setAttachments(prev => [...prev, ...onlyImages]);
        }
    };
    const handlePaste = useCallback(async (e: ClipboardEvent) => {
        const dt = e.clipboardData;
        if (!dt) return;

        const items = Array.from(dt.items || []);
        const imageItems = items.filter(it => it.kind === "file" && it.type.startsWith("image/"));

        if (imageItems.length) {
            e.preventDefault();
            const files: File[] = [];
            for (const it of imageItems) {
                const f = it.getAsFile();
                if (f) {
                    const safeName = f.name?.trim() || `pasted-${Date.now()}.${(f.type.split("/")[1] || "png")}`;
                    files.push(new File([f], safeName, { type: f.type }));
                }
            }
            applyPastedFiles(files);
            return;
        }

        const htmlItem = items.find(it => it.type === "text/html");
        if (htmlItem) {
            htmlItem.getAsString(async (html) => {
                const src = extractFirstImgSrcFromHtml(html);
                if (!src) return;
                let file: File | null = null;

                if (src.startsWith("data:image/")) {
                    file = dataUrlToFile(src);
                } else if (/^https?:\/\//i.test(src)) {
                    file = await urlToFile(src);
                }
                if (file) {
                    e.preventDefault();
                    applyPastedFiles([file]);
                }
            });
            return;
        }

        const text = dt.getData("text/plain");
        if (text && /^https?:\/\/.+\.(png|jpe?g|webp|gif|bmp|svg)(\?.*)?$/i.test(text)) {
            const file = await urlToFile(text);
            if (file) {
                e.preventDefault();
                applyPastedFiles([file]);
            }
        }
    }, [isSupportRole]);

    useEffect(() => {
        const onPasteDoc = (evt: ClipboardEvent) => {
            const root = rootRef.current;
            if (!root) return;
            const target = evt.target as Node | null;
            if (target && !root.contains(target)) return;
            handlePaste(evt);
        };
        document.addEventListener("paste", onPasteDoc as any);
        return () => document.removeEventListener("paste", onPasteDoc as any);
    }, [handlePaste]);

    useEffect(() => {
        const insideRootByPoint = (e: DragEvent) => {
            const root = rootRef.current;
            if (!root) return false;
            const rect = root.getBoundingClientRect();
            const x = e.clientX;
            const y = e.clientY;
            return x >= rect.left && x <= rect.right && y >= rect.top && y <= rect.bottom;
        };

        const onDragOver = (e: DragEvent) => {
            if (insideRootByPoint(e)) {
                e.preventDefault();
                e.stopPropagation();
                if (e.dataTransfer) e.dataTransfer.dropEffect = "copy";
                setIsDraggingOver(true);
            } else {
                setIsDraggingOver(false);
            }
        };

        const onDragEnter = (e: DragEvent) => {
            if (insideRootByPoint(e)) {
                e.preventDefault();
                e.stopPropagation();
                setIsDraggingOver(true);
            }
        };

        const onDragLeave = (e: DragEvent) => {
            if (!insideRootByPoint(e)) {
                setIsDraggingOver(false);
            }
        };

        const onDrop = (e: DragEvent) => {
            const inside = insideRootByPoint(e);
            if (inside) {
                e.preventDefault();
                e.stopPropagation();
                const files = Array.from(e.dataTransfer?.files || []).filter(f => /^image\//.test(f.type));
                if (files.length) {
                    if (!isSupportRole) setAttachments([files[0]]);
                    else setAttachments(prev => [...prev, ...files]);
                }
            }
            setIsDraggingOver(false);
        };

        document.addEventListener("dragenter", onDragEnter as any);
        document.addEventListener("dragover", onDragOver as any);
        document.addEventListener("dragleave", onDragLeave as any);
        document.addEventListener("drop", onDrop as any);

        return () => {
            document.removeEventListener("dragenter", onDragEnter as any);
            document.removeEventListener("dragover", onDragOver as any);
            document.removeEventListener("dragleave", onDragLeave as any);
            document.removeEventListener("drop", onDrop as any);
        };
    }, [isSupportRole]);


    const [isPluginType, setIsPluginType] = useState<boolean>(false);

    const intervalIdRef = useRef<any>(null);
    const isTokenExpired = (token: string): boolean => {
        try {
            const payload = JSON.parse(atob(token.split('.')[1]));
            return Date.now() / 1000 >= (payload.exp ?? 0);
        } catch {
            return true;
        }
    };

    const checkSessionError = async (exception: any) => {
        if (exception instanceof SessionError) {
            closeSession();
        }
    }
    const startIntervalConversation = async () => {
        if (!intervalIdRef.current) {
            loadConversation();
            const id = setInterval(loadConversation, 20 * 1000);
            intervalIdRef.current = id;
        }
    };
    const cancelIntervalConversation = () => {
        if (intervalIdRef.current) {
            clearInterval(intervalIdRef.current);
            intervalIdRef.current = null;
        }
    };

    // const onLoadSession = () => {
    //     console.log("on storage change")

    //     const helpMode = sessionStorage.getItem('helpMode');

    //     console.log(helpMode)
    //     if (helpMode) {
    //         console.log(JSON.parse(helpMode).role == "support")
    //         loadConversation();
    //         console.log(isSupportRole)
    //         startIntervalConversation();
    //     }
    // };

    const closeSession = () => {
        currentToken.current = undefined;
        sessionStorage.clear();
        cancelIntervalConversation();
        setConversationLimitReached(false);
        setLimitMessage("");
        setAnswers([]);
        closeEvent();

        if ((window as any).ChatWidget?.close) {
            (window as any).ChatWidget.close();
        }
        if ((window as any).ChatWidget?.unmount) {
            setTimeout(() => {
                (window as any).ChatWidget?.unmount?.(".kust-chat-widget-container");
            }, 350);
        }
    }
    const getQuestions = async (id: string) => {
        setIsPageReady(false)
        try {
            const hadExistingSession = !!sessionStorage.getItem('accessToken');
            const tokenRes = await checkSession(id)
            if (tokenRes && tokenRes !== "")
                currentToken.current = tokenRes

            const abortController = new AbortController();
            abortFuncs.current.unshift(abortController);
            const token = currentToken.current && currentToken.current != "" ? currentToken.current : sessionStorage.getItem('accessToken') as string;
            const botSetup = botSettings ? botSettings : await getBotSetup(abortController.signal, token);
            setQuestions(botSetup.questions);
            if (botSetup.themeColor)
                setMainColor(botSetup.themeColor);
            if (botSetup.allowIntroduce) {
                setAllowIntroduce(true);
                setIntroduceText(botSetup.introduceText);
            }
            setAllowFaq(botSetup.allowFaq !== false);

            if (hadExistingSession && !tokenRes && currentToken.current) {
                await loadConversation();
            }
        } catch (e) {
            checkSessionError(e);
            console.log(e)
        } finally {
            setIsPageReady(true)
        }
    }
    const loadConversation = async () => {
        if (showLoadingMessage || isLoading || isAssistantPending) return;
        try {
            const abortController = new AbortController();
            abortFuncs.current.unshift(abortController);

            if (!currentToken.current || currentToken.current != "") {
                currentToken.current = sessionStorage.getItem('accessToken') as string;
            }

            const { messages, ticket_status, helped } =
                await getConversationMessages(
                    abortController.signal,
                    currentToken.current && currentToken.current != ""
                        ? currentToken.current
                        : (sessionStorage.getItem('accessToken') as string)
                );

            if (ticket_status === 'closed') {
                closeSession();
                return;
            }

            if (Array.isArray(messages) && messages.length > 0) {
                setAnswers(messages);
                if (messages[messages.length - 1].role === "user") {
                    lastQuestionRef.current = messages[messages.length - 1].content;
                }
                const supportMessageExists = messages.some((m: Message) => m.role === 'support');
                if (supportMessageExists) setSystemMessage('');
            }

            setIsHelpMode(!!helped);
            setIsSupportRole(JSON.parse(sessionStorage.getItem('helpMode') || '{"role":"user"}').role === 'support');

        } catch (e) {
            checkSessionError(e);
            console.log(e);
        } finally {
            setIsLoading(false);
        }
    };
    const checkSession = async (id?: string) => {
        try {
            const sessionToken = sessionStorage.getItem('accessToken') as string
            if (sessionToken) {
                if (isTokenExpired(sessionToken)) {
                    sessionStorage.removeItem('accessToken');
                    currentToken.current = undefined;
                } else {
                    currentToken.current = sessionToken
                    return
                }
            }
            if (!webId && !id)
                return

            const helpMode = sessionStorage?.getItem('helpMode');
            const helpObj: any = helpMode ? JSON.parse(helpMode) : null

            // if (!helpMode || helpObj?.role == 'user') {
            //     setIsHelpMode(false)
            //     sessionStorage.clear();
            // }
            // closeEvent();
            const tokenRes = await validateId(webId || id);
            if (!tokenRes)
                return closeSession()
            currentToken.current = tokenRes
            sessionStorage.setItem('accessToken', tokenRes);
            setHelpShowed(true);
            startIntervalConversation();
            return tokenRes
        } catch (e) {
            console.log(e)
        }
    }

    const sendQuestionRequest = async (question: string) => {
        const tokenRes = await checkSession()
        if (tokenRes && tokenRes !== "")
            currentToken.current = tokenRes


        if (!isHelpMode) {
            return makeGPTApiRequest(question)
        }
        return sendSupportRequest(question)
    }
    const sendSupportRequest = async (question: string) => {
        lastQuestionRef.current = question;
        setIsLoading(true);
        const abortController = new AbortController();
        abortFuncs.current.unshift(abortController);
        try {
            const role = isSupportRole ? Role.SUPPORT : Role.USER;

            if (!currentToken.current || currentToken.current === "") {
                currentToken.current = sessionStorage.getItem("accessToken") as string;
            }
            const tokenToUse =
                currentToken.current && currentToken.current !== ""
                    ? currentToken.current
                    : (sessionStorage.getItem("accessToken") as string);

            const filesToSend = role === Role.USER ? attachments.slice(0, 1) : attachments.slice();

            const optimisticMessage: ChatMessage & { attachments?: Attachment[] } = {
                role,
                content: question,
                ...(filesToSend.length
                    ? {
                        attachments: filesToSend.map((f) => ({
                            type: "image",
                            __localPreview: URL.createObjectURL(f),
                            uploading: true,
                        })),
                    }
                    : {}),
            };

            setAnswers((prev) => [...prev, optimisticMessage]);
            setAttachments([]);

            let response: Response;
            if (filesToSend.length > 0) {
                response = await fetchConversationApiMultipart(
                    { message: question, role, files: filesToSend },
                    abortController.signal,
                    tokenToUse
                );
            } else {
                const requestSave: ConversationSaveRequest = {
                    message_id: Math.max(0, answers.length - 1),
                    message: question,
                    session_id: SESSION_ID.toString(),
                    role,
                };
                response = await fetchConversationApi(requestSave, abortController.signal, tokenToUse);
            }

            if (response.status === 401 || response.status === 404) {
                const errorMessage = "Unauthorized";
                setError(true);
                setHelpDisabled(true);
                setAnswers((prev) => [
                    ...prev,
                    {
                        role: "error",
                        content: errorMessage,
                    },
                ]);
                return closeSession();
            } else if (response.status === 403) {
                const errorMessage = "Forbidden: Access is denied from this domain.";
                setError(true);
                setHelpShowed(false);
                setAnswers((prev) => [
                    ...prev,
                    {
                        role: "error",
                        content: errorMessage,
                    },
                ]);
                closeSession();
                return;
            } else if (response.status === 429) {
                await handle429Response(response);
                return;
            }

            setAttachments([]);
            await loadConversation();

            chatMessageStreamEnd.current?.scrollIntoView({ behavior: "smooth" });
            abortFuncs.current = abortFuncs.current.filter((a) => a !== abortController);
        } catch (e) {
            checkSessionError(e)
        } finally {
            setIsLoading(false);
            return abortController.abort();
        }
    }

    const streamAgentResponse = async (
        response: Response,
        onChunk: (data: string) => void,
        signal?: AbortSignal
    ) => {
        if (!response.body) return;
        const reader = response.body.getReader();
        const decoder = new TextDecoder("utf-8");
        let buffer = "";

        try {
            while (true) {
                if (signal?.aborted) {
                    try { await reader.cancel(); } catch { }
                    break;
                }
                const { done, value } = await reader.read();
                if (done) break;

                buffer += decoder.decode(value, { stream: true });

                let idx = buffer.indexOf("\n\n");
                while (idx !== -1) {
                    const rawEvent = buffer.slice(0, idx);
                    buffer = buffer.slice(idx + 2);
                    idx = buffer.indexOf("\n\n");

                    const lines = rawEvent.split("\n");
                    const dataLines: string[] = [];

                    for (const ln of lines) {
                        if (!ln.startsWith("data:")) continue;
                        const data = ln.startsWith("data: ") ? ln.slice(6) : ln.slice(5);
                        dataLines.push(data);
                    }

                    if (dataLines.length) {
                        const payload = dataLines.join("\n");
                        if (payload.trim() === "[[DONE]]") return;
                        onChunk(payload);
                    }
                }
            }
        } finally {
            try { await reader.cancel(); } catch { }
        }
    };

    const makeGPTApiRequest = async (question: string) => {
        lastQuestionRef.current = question;
        const filesToSend = attachments.slice(0, 1);
        const pushedUserOptimistic = filesToSend.length > 0;

        setIsLoading(true);
        setShowLoadingMessage(true);

        const abortController = new AbortController();
        abortFuncs.current.unshift(abortController);

        const userMessage: ChatMessage = { role: "user", content: question };
        let result = {} as ChatResponse;

        try {
            const wasIntervalRunning = !!intervalIdRef.current;
            if (wasIntervalRunning) cancelIntervalConversation();
            setPendingGPTRepsonse(true);
            if (!currentToken.current || currentToken.current === "") {
                currentToken.current = sessionStorage.getItem("accessToken") as string;
            }
            const tokenToUse =
                currentToken.current && currentToken.current !== ""
                    ? currentToken.current
                    : (sessionStorage.getItem("accessToken") as string);

            if (filesToSend.length > 0) {
                const optimisticMessage: ChatMessage & { attachments?: Attachment[] } = {
                    role: Role.USER,
                    content: question,
                    attachments: filesToSend.map((f) => ({
                        type: "image",
                        __localPreview: URL.createObjectURL(f),
                        uploading: true,
                    })),
                };
                setAnswers((prev) => [...prev, optimisticMessage]);
                const upRes = await fetchConversationApiMultipart(
                    { message: question, role: Role.USER, files: filesToSend },
                    abortController.signal,
                    tokenToUse
                );

                if (upRes.status === 401 || upRes.status === 404) {
                    const errorMessage = "Unauthorized";
                    setError(true);
                    setHelpDisabled(true);
                    setAnswers((prev) => [...prev, { role: "system", content: errorMessage }]);
                    return closeSession();
                } else if (upRes.status === 403) {
                    const errorMessage = "Forbidden: Access is denied from this domain.";
                    setError(true);
                    setHelpDisabled(true);
                    setAnswers((prev) => [...prev, { role: "system", content: errorMessage }]);
                    return closeSession();
                } else if (upRes.status === 429) {
                    await handle429Response(upRes);
                    return;
                }

                setAttachments([]);
            }

            const request: ConversationRequest = pushedUserOptimistic
                ? { messages: [] }
                : { messages: [...answers.filter((a) => a.role !== "error"), userMessage] };
            const response = await conversationApi(request, abortController.signal, tokenToUse);

            if (response && response.status === 503) {
                const errorMessage = "Sorry, server is overloaded, please try again later.";
                setAnswers((prev) => [
                    ...prev,
                    ...(pushedUserOptimistic ? [] : [userMessage]),
                    { role: "system", content: errorMessage },
                ]);
                setShowLoadingMessage(false);
                return;
            }
            if (response.status === 401 || response.status === 404) {
                const errorMessage = "Unauthorized";
                setError(true);
                setHelpDisabled(true);
                setAnswers((prev) => [
                    ...prev,
                    ...(pushedUserOptimistic ? [] : [userMessage]),
                    { role: "system", content: errorMessage },
                ]);
                return closeSession();
            } else if (response.status === 403) {
                const errorMessage = "Forbidden: Access is denied from this domain.";
                setError(true);
                setHelpDisabled(true);
                setAnswers((prev) => [
                    ...prev,
                    ...(pushedUserOptimistic ? [] : [userMessage]),
                    { role: "system", content: errorMessage },
                ]);
                return closeSession();
            } else if (response.status === 429) {
                await handle429Response(response);
                setShowLoadingMessage(false);
                return;
            }

            let runningText = "";
            if (response?.body) {
                await streamAgentResponse(
                    response,
                    (dataChunk: string) => {
                        runningText += dataChunk;
                        if (runningText.trim().length === 0) {
                            return;
                        }
                        setAnswers((prev) => {
                            let base = [...prev];
                            if (!pushedUserOptimistic) {
                                const hasUserAlready = base.some(
                                    (m) => m.role === "user" && m.content === userMessage.content
                                );
                                if (!hasUserAlready) {
                                    base = [...base, userMessage];
                                }
                            }

                            const last = base[base.length - 1];
                            if (last && last.role === "assistant") {
                                return [...base.slice(0, -1), { role: "assistant", content: runningText }];
                            }
                            return [...base, { role: "assistant", content: runningText }];
                        });

                        setShowLoadingMessage(false);

                        const KUST_JSON_REGEX_GLOBAL = /\[\[KUST_JSON\]\]([\s\S]*?)\[\[KUST_JSON\]\]/g;
                        const textWithoutJson = runningText.replace(KUST_JSON_REGEX_GLOBAL, "").trim();
                        if (textWithoutJson.length === 0) {
                            setShowLoadingMessage(true);
                        }

                        if (chatMessageStreamRef.current) {
                            chatMessageStreamRef.current.scrollTop = chatMessageStreamRef.current.scrollHeight;
                        }
                        chatMessageStreamEnd.current?.scrollIntoView({ behavior: "smooth" });
                    },
                    abortController.signal
                );

                const KUST_JSON_REDIRECT_REGEX = /\[\[KUST_JSON\]\]([\s\S]*?)\[\[KUST_JSON\]\]/;
                const redirectMatch = runningText.match(KUST_JSON_REDIRECT_REGEX);
                if (redirectMatch) {
                    try {
                        const redirectData = JSON.parse(redirectMatch[1]);
                        if (redirectData.type === "redirect" && redirectData.url) {
                            const botMessage = redirectData.message || "Redirecting you…";
                            const redirectResult: ChatMessage = { role: "assistant", content: botMessage };

                            setAnswers((prev) => {
                                let base = [...prev];
                                if (!pushedUserOptimistic) {
                                    const hasUserAlready = base.some(
                                        (m) => m.role === "user" && m.content === userMessage.content
                                    );
                                    if (!hasUserAlready) base = [...base, userMessage];
                                }
                                const last = base[base.length - 1];
                                if (last && last.role === "assistant") {
                                    return [...base.slice(0, -1), redirectResult];
                                }
                                return [...base, redirectResult];
                            });
                            setShowLoadingMessage(false);
                            setRedirectPending({ url: redirectData.url, message: botMessage, animation_title: redirectData.animation_title || "Redirecting" });

                            const baseForSave = [...answers];
                            const hasUserInAnswers = baseForSave.some(
                                (m) => m.role === "user" && m.content === userMessage.content
                            );
                            const saveArr = hasUserInAnswers ? baseForSave : [...baseForSave, userMessage];
                            await fetchConversationApi(
                                createRequestSave(saveArr, userMessage),
                                abortController.signal,
                                currentToken.current!
                            );
                            await fetchConversationApi(
                                createAnswerSave([...saveArr, redirectResult], redirectResult as any),
                                abortController.signal,
                                currentToken.current!
                            );

                            sessionStorage.setItem('kust_redirect_pending', '1');
                            await new Promise<void>((resolve) => setTimeout(resolve, 2500));
                            window.location.href = redirectData.url;
                            return;
                        }
                    } catch { }
                }

                const finalResult: ChatMessage = { role: "assistant", content: runningText };

                if (pushedUserOptimistic) {
                    const convArr = [...answers, finalResult];
                    const requestSaveAnswer: ConversationSaveRequest = createAnswerSave(convArr as any, finalResult as any);
                    const answerSaveRes = await fetchConversationApi(requestSaveAnswer, abortController.signal, currentToken.current!);
                    try {
                        const answerSaveBody = await answerSaveRes.json();
                        if (answerSaveBody?.limit_reached) {
                            setConversationLimitReached(true);
                            setLimitMessage("You've reached the conversation limit. Please start a new session.");
                        }
                    } catch { }
                } else {
                    const convArr = [...answers];
                    const hasUserInAnswers = convArr.some(
                        (m) => m.role === "user" && m.content === userMessage.content
                    );
                    const baseForSave = hasUserInAnswers ? convArr : [...convArr, userMessage];
                    const requestSave: ConversationSaveRequest = createRequestSave(baseForSave, userMessage);
                    const requestSaveAnswer: ConversationSaveRequest = createAnswerSave(baseForSave, finalResult as any);
                    await fetchConversationApi(requestSave, abortController.signal, currentToken.current!);
                    const answerSaveRes = await fetchConversationApi(requestSaveAnswer, abortController.signal, currentToken.current!);
                    try {
                        const answerSaveBody = await answerSaveRes.json();
                        if (answerSaveBody?.limit_reached) {
                            setConversationLimitReached(true);
                            setLimitMessage("You've reached the conversation limit. Please start a new session.");
                        }
                    } catch { }
                }

                setTimeout(() => {
                    if (chatMessageStreamRef.current) {
                        chatMessageStreamRef.current.scrollTop = chatMessageStreamRef.current.scrollHeight;
                    }
                }, 100);
                chatMessageStreamEnd.current?.scrollIntoView({ behavior: "smooth" });
                setPendingGPTRepsonse(false);
            }
        } catch (e) {
            checkSessionError(e);
            if (!abortController.signal.aborted) {
                let errorMessage =
                    "An error occurred. Please try again. If the problem persists, please contact the site administrator.";
                if ((result as any).error?.message) errorMessage = (result as any).error.message;
                else if (typeof (result as any).error === "string") errorMessage = (result as any).error;

                setAnswers((prev) => [
                    ...prev,
                    ...(pushedUserOptimistic ? [] : [userMessage]),
                    { role: "system", content: errorMessage },
                ]);

                const convArr = pushedUserOptimistic ? answers : [...answers, userMessage];
                const requestSave: ConversationSaveRequest = {
                    message_id: convArr.length > 0 ? convArr.length - 1 : 0,
                    message: userMessage.content,
                    session_id: SESSION_ID.toString(),
                    role: Role.USER,
                };
                await fetchConversationApi(requestSave, abortController.signal, currentToken.current!);

                const requestSaveAnswer: ConversationSaveRequest = {
                    message_id: convArr.length > 0 ? convArr.length : 0,
                    message: errorMessage,
                    session_id: SESSION_ID.toString(),
                    role: Role.ERROR,
                };
                await fetchConversationApi(requestSaveAnswer, abortController.signal, currentToken.current!);
            } else {
                const convArr = pushedUserOptimistic ? answers : [...answers, userMessage];
                setAnswers(convArr);
                const requestSave: ConversationSaveRequest = {
                    message_id: convArr.length > 0 ? convArr.length - 1 : 0,
                    message: userMessage.content,
                    session_id: SESSION_ID.toString(),
                    role: Role.USER,
                };
                await fetchConversationApi(requestSave, abortController.signal, currentToken.current!);
            }
        } finally {
            setIsLoading(false);
            setShowLoadingMessage(false);
            setPendingGPTRepsonse(false);
            try {
                if (!intervalIdRef.current) startIntervalConversation();
            } catch { }
            abortFuncs.current = abortFuncs.current.filter((a) => a !== abortController);
        }

        return abortController.abort();
    };
    const createRequestSave = (convArr: any, userMessage: any): ConversationSaveRequest => {
        const request: ConversationSaveRequest = {
            message_id: convArr.length > 0 ? convArr.length - 2 : 0,
            message: userMessage.content,
            // TODO delete session_id, in the API replace for the webticket_id
            session_id: SESSION_ID.toString(),
            role: Role.USER
        };
        return request
    }
    const createAnswerSave = (convArr: any[], result: ChatMessage): ConversationSaveRequest => {
        const request: ConversationSaveRequest = {
            message_id: convArr.length > 0 ? convArr.length - 1 : 0,
            message: result.content,
            session_id: SESSION_ID.toString(),
            role: Role.ASSISTANT
        };
        return request
    }

    const stopGenerating = () => {
        abortFuncs.current.forEach(a => a.abort());
        setShowLoadingMessage(false);
        setIsLoading(false);
    }

    useEffect(() => {
        currentToken.current = sessionStorage.getItem('accessToken') || "";
        setConversationLimitReached(false);
        setLimitMessage("");
        return () => {
            if (replyPollTimeoutRef.current) {
                clearTimeout(replyPollTimeoutRef.current);
            }
        }
    }, []);


    useEffect(() => {
        setIsPageReady(false)

        // Check for type=plugin parameter from script (props, data attributes, or script URL params)
        let typeParam: string | null = null;

        // First check if type was passed as a prop
        if (type) {
            typeParam = type;
        } else {
            // Check script tag data attributes (e.g., data-type="plugin")
            const scriptTag = Array.from(document.querySelectorAll("script"))
                .find(s => s.src && s.src.includes("widget/chat-widget.iife.js"));

            if (scriptTag) {
                // Check data-type attribute
                typeParam = scriptTag.getAttribute('type') || scriptTag.getAttribute('data-type');

                // If not found, check script URL query params (e.g., ?type=plugin)
                if (!typeParam) {
                    try {
                        const scriptUrl = new URL(scriptTag.src);
                        typeParam = scriptUrl.searchParams.get('type');
                    } catch (e) {
                        // If URL parsing fails, continue
                    }
                }
            }
        }

        setIsPluginType(typeParam === 'plugin');

        // setToken('');
        // getUserInfoList();
        // sessionStorage.clear();
        const fetchData = async () => {
            try {
                if (!id) {
                    console.error("Error")
                } else {

                    // if (currentToken.current && currentToken.current != "") {
                    //     setIsPageReady(true)
                    //     return currentToken.current
                    // }
                    // const validatedToken = await validateId(id);
                    // if (validatedToken) {
                    //     setToken(validatedToken)
                    //     sessionStorage.setItem('accessToken', validatedToken);
                    // }
                    getQuestions(id!);
                    setWebId(id!)
                    setIsHelpMode(false)
                }
            } catch (error) {
                checkSessionError(error)
                console.error("Error validating ID:", error);
            }
        };
        fetchData();
    }, [id]);

    // Add this effect to scroll to bottom when answers change or loading state changes
    useEffect(() => {
        if (chatMessageStreamRef.current) {
            chatMessageStreamRef.current.scrollTop = chatMessageStreamRef.current.scrollHeight;
        }
    }, [answers, isLoading, showLoadingMessage]);

    const onShowCitation = (citation: Citation) => {
        setActiveCitation([citation.content, citation.id, citation.title ?? "", citation.filepath ?? "", "", ""]);
        setIsCitationPanelOpen(true);
    };

    const parseCitationFromMessage = (message: ChatMessage) => {
        if (message.role === "tool") {
            try {
                const toolMessage = JSON.parse(message.content) as ToolMessageContent;
                return toolMessage.citations;
            }
            catch {
                return [];
            }
        }
        return [];
    }
    const validateId = async (webId?: string): Promise<string | undefined> => {
        const abortController = new AbortController();
        abortFuncs.current.unshift(abortController);
        if (webId) {
            try {
                const createWebTicket = await createTicketSession({ web_id: webId }, abortController.signal)
                return createWebTicket.token
            } catch (e) {
                checkSessionError(e)
            } finally {
                abortFuncs.current = abortFuncs.current.filter(a => a !== abortController);
            }
        }
    }
    const onPickFiles = () => fileInputRef.current?.click();

    const onFilesSelected = (e: React.ChangeEvent<HTMLInputElement>) => {
        if (!e.target.files) return;
        const files = Array.from(e.target.files).filter((f) => /^image\//.test(f.type));
        const isUserRole = !isSupportRole;
        if (isUserRole) {
            setAttachments(files.length ? [files[0]] : []);
        } else {
            setAttachments((prev) => [...prev, ...files]);
        }
    };

    const removeAttachmentAt = (idx: number) => {
        setAttachments((prev) => prev.filter((_, i) => i !== idx));
    };
    const renderAttachments = (msg: any) => {
        const atts: any[] = Array.isArray(msg?.attachments) ? msg.attachments : [];
        if (!atts.length) return null;

        const srcFor = (a: any) => {
            if (a?.__localPreview) return a.__localPreview as string;
            const key = a?.thumb?.key || a?.original?.key;
            return key ? fileUrl(key) : "";
        };

        if (atts.length === 1) {
            const a = atts[0];
            const linkKey = a?.original?.key || a?.thumb?.key;
            const clickUrl = linkKey ? fileUrl(linkKey) : srcFor(a);

            const src = srcFor(a);
            if (!src) return null;

            return (
                <div className={styles.attSingle} onClick={() => window.open(clickUrl, "_blank")}>
                    <img
                        className={styles.attSingleImg}
                        src={src}
                        alt="attachment"
                        loading="lazy"
                        onLoad={() => a?.__localPreview && URL.revokeObjectURL(a.__localPreview)}
                    />
                </div>
            );
        }

        return (
            <div className={styles.attGrid}>
                {atts.map((a, i) => {
                    const linkKey = a?.original?.key || a?.thumb?.key;
                    const clickUrl = linkKey ? fileUrl(linkKey) : srcFor(a);
                    const src = srcFor(a);
                    if (!src) return null;

                    return (
                        <div
                            key={`att-${i}`}
                            className={styles.attItem}
                            onClick={() => window.open(clickUrl, "_blank")}
                            title={`attachment-${i + 1}`}
                        >
                            <img
                                className={styles.attItemImg}
                                src={src}
                                alt={`attachment-${i + 1}`}
                                loading="lazy"
                                onLoad={() => a?.__localPreview && URL.revokeObjectURL(a.__localPreview)}
                            />
                        </div>
                    );
                })}
            </div>
        );
    };


    return isPageReady ? (
        <Layout botSettings={botSettings}>
            <HelpProvider>
                <div ref={rootRef} className={styles.rootContainer} role="root">
                    {isDraggingOver && (
                        <div className={styles.dropOverlay} aria-hidden="true">
                            <div className={styles.dropOverlayLabel}>Drop images to attach</div>
                        </div>
                    )}
                    {!isChatOpen && (
                        <div className={styles.container} role="main">
                            <Stack horizontal className={styles.chatRoot}>
                                <div className={styles.chatContainer}>
                                    {(answers.length === 0 && !showLoadingMessage) ? (
                                        <Stack className={styles.chatEmptyState} style={{ height: "100%", width: "100%" }}>
                                            {!(allowIntroduce && introduceText) && (
                                                <div style={{ position: "relative", top: "50px" }}>
                                                    <div
                                                        className={styles.iconWrapper}
                                                        style={{ margin: "0 auto", ...(mainColor && { backgroundColor: mainColor }) }}
                                                    >
                                                        <img src={Kust_White} className={styles.chatIcon} aria-hidden="true" />
                                                    </div>

                                                    <p className={styles.chatEmptyStateTitle}>Start chatting</p>
                                                </div>
                                            )}

                                            {allowIntroduce && introduceText && (
                                                <div
                                                    style={{
                                                        width: "100%",
                                                        display: "flex",
                                                        justifyContent: "left",
                                                    }}
                                                >
                                                    <div className={styles.chatMessageGpt} style={{ maxWidth: "80%" }}>
                                                        <div
                                                            style={{
                                                                padding: "12px 16px",
                                                                backgroundColor: "#38393a",
                                                                borderRadius: "8px",
                                                                fontSize: "14px",
                                                                lineHeight: "1.4",
                                                            }}
                                                        >
                                                            {introduceText}
                                                        </div>
                                                    </div>
                                                </div>
                                            )}

                                            <div
                                                style={{
                                                    marginTop: "auto",
                                                    paddingBottom: "20px",
                                                    display: "flex",
                                                    flexDirection: "row",
                                                    flexWrap: "wrap",
                                                    justifyContent: "center",
                                                    gap: "10px",
                                                    maxWidth: "100%",
                                                }}
                                            >
                                                {allowFaq &&
                                                    questions.map((question, index) => (
                                                        <div
                                                            key={`question-${index}`}
                                                            className={styles.chatEmptyStateQuestion}
                                                            onClick={() => sendQuestionRequest(question)}
                                                            role="button"
                                                            tabIndex={0}
                                                            onKeyDown={(e) => e.key === "Enter" && sendQuestionRequest(question)}
                                                        >
                                                            {question}
                                                        </div>
                                                    ))}
                                            </div>
                                        </Stack>
                                    ) : (
                                        <div
                                            ref={chatMessageStreamRef}
                                            className={styles.chatMessageStream}
                                            style={{ marginBottom: isLoading && !isHelpMode ? "40px" : "30px" }}
                                            role="log"
                                        >
                                            {answers.map((answer, index) => (
                                                <React.Fragment key={`msg-${index}`}>
                                                    {answer.role === "user" ? (
                                                        <div
                                                            className={isSupportRole ? styles.chatMessageGpt : styles.chatMessageUser}
                                                            tabIndex={0}
                                                        >
                                                            <div className={styles.msgBlockRight}>
                                                                {renderAttachments(answer)}
                                                                {(answer.content && answer.content.trim() !== "") && (
                                                                    <div
                                                                        style={isSupportRole ? { background: "#38393a" } : {}}
                                                                        className={styles.chatMessageUserMessage}
                                                                    >
                                                                        {answer.content}
                                                                    </div>
                                                                )}
                                                            </div>
                                                        </div>
                                                    ) : answer.role === "support" ? (
                                                        <div
                                                            className={!isSupportRole ? styles.chatMessageGpt : styles.chatMessageUser}
                                                            tabIndex={0}
                                                        >
                                                            <div className={styles.msgBlockLeft}>
                                                                {(answer.content && answer.content.trim() !== "") && renderAttachments(answer)}
                                                                <div className={styles.gptRow}>
                                                                    <img
                                                                        src={Operator}
                                                                        className={styles.chatIcon}
                                                                        style={{ width: "18px", height: "18px", verticalAlign: "middle" }}
                                                                        aria-hidden="true"
                                                                    />
                                                                    {(answer.content && answer.content.trim() !== "") ? (
                                                                        <Answer
                                                                            answer={{
                                                                                answer: answer.content,
                                                                                citations: parseCitationFromMessage(answers[index - 1]),
                                                                            }}
                                                                            answers={answers}
                                                                            pos={index}
                                                                            onCitationClicked={(c) => onShowCitation(c)}
                                                                        />
                                                                    ) : (
                                                                        renderAttachments(answer)
                                                                    )}
                                                                </div>
                                                            </div>
                                                        </div>
                                                    ) : answer.role === "assistant" ? (
                                                        <div className={!isSupportRole ? styles.chatMessageGpt : styles.chatMessageUser}>
                                                            <div className={styles.msgBlockLeft}>
                                                                {renderAttachments(answer)}
                                                                <div className={styles.gptRow}>
                                                                    {!isSupportRole && (
                                                                        <img
                                                                            src={Kust_Blue}
                                                                            className={styles.chatIcon}
                                                                            style={{ width: "18px", height: "18px", verticalAlign: "middle" }}
                                                                            aria-hidden="true"
                                                                        />
                                                                    )}
                                                                    {isSupportRole && (
                                                                        <img
                                                                            src={Operator}
                                                                            className={styles.chatIcon}
                                                                            style={{ width: "18px", height: "18px", verticalAlign: "middle" }}
                                                                            aria-hidden="true"
                                                                        />
                                                                    )}
                                                                    <Answer
                                                                        answer={{
                                                                            answer: answer.content,
                                                                            citations: parseCitationFromMessage(answers[index - 1]),
                                                                        }}
                                                                        answers={answers}
                                                                        pos={index}
                                                                        onCitationClicked={(c) => onShowCitation(c)}
                                                                    />
                                                                </div>
                                                            </div>
                                                        </div>
                                                    ) : answer.role === "system" || answer.role === "error" ? (
                                                        <div className={styles.chatMessageGpt}>
                                                            <div className={styles.msgBlockLeft}>
                                                                <div className={styles.gptRow}>
                                                                    {!isSupportRole && (
                                                                        <img
                                                                            src={Kust_Blue}
                                                                            className={styles.chatIcon}
                                                                            style={{ width: "18px", height: "18px", verticalAlign: "middle" }}
                                                                            aria-hidden="true"
                                                                        />
                                                                    )}
                                                                    <Answer
                                                                        answer={{
                                                                            answer: answer.content,
                                                                            citations: parseCitationFromMessage(answers[index - 1]),
                                                                        }}
                                                                        answers={answers}
                                                                        pos={index}
                                                                        onCitationClicked={(c) => onShowCitation(c)}
                                                                    />
                                                                </div>
                                                            </div>
                                                        </div>
                                                    ) : null}
                                                </React.Fragment>
                                            ))}

                                            {showLoadingMessage && (() => {
                                                const last = answers[answers.length - 1];
                                                const secondLast = answers[answers.length - 2];

                                                let hideUserEcho = false;

                                                if (last && last.role === "user" && last.content === lastQuestionRef.current) {
                                                    hideUserEcho = true;
                                                }
                                                else if (last && last.role === "assistant" && secondLast && secondLast.role === "user" && secondLast.content === lastQuestionRef.current) {
                                                    hideUserEcho = true;
                                                }

                                                return (
                                                    <>
                                                        {!hideUserEcho && (
                                                            <div className={styles.chatMessageUser} style={{ marginBottom: "30px" }}>
                                                                <div className={styles.msgBlockRight}>
                                                                    <div className={styles.chatMessageUserMessage}>{lastQuestionRef.current}</div>
                                                                </div>
                                                            </div>
                                                        )}
                                                        <div
                                                            className={styles.chatMessageGpt}
                                                            style={hideUserEcho ? { marginTop: "50px" } : {}}
                                                        >
                                                            <div className={styles.msgBlockLeft}>
                                                                <div className={styles.gptRow}>
                                                                    <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                                                                        <div
                                                                            style={{
                                                                                width: "4px",
                                                                                height: "4px",
                                                                                borderRadius: "50%",
                                                                                background: "#000",
                                                                                animation: "bounce 1s infinite",
                                                                            }}
                                                                        ></div>
                                                                        <div
                                                                            style={{
                                                                                width: "4px",
                                                                                height: "4px",
                                                                                borderRadius: "50%",
                                                                                background: "#000",
                                                                                animation: "bounce 1s infinite .2s",
                                                                            }}
                                                                        ></div>
                                                                        <div
                                                                            style={{
                                                                                width: "4px",
                                                                                height: "4px",
                                                                                borderRadius: "50%",
                                                                                background: "#000",
                                                                                animation: "bounce 1s infinite .4s",
                                                                            }}
                                                                        ></div>
                                                                        <style>{`
                                          @keyframes bounce {
                                            0%, 100% { transform: translateY(0); }
                                            50% { transform: translateY(-6px); }
                                          }
                                        `}</style>
                                                                    </div>
                                                                </div>
                                                            </div>
                                                        </div>
                                                    </>
                                                );
                                            })()}
                                            {redirectPending && (
                                                <div className={styles.redirectBanner}>
                                                    <div className={styles.redirectBannerBody}>
                                                        <div className={styles.redirectBannerIcon}>
                                                            <ArrowUpRightRegular style={{ width: 22, height: 22, color: "#fff" }} />
                                                        </div>
                                                        <div className={styles.redirectBannerText}>
                                                            <div className={styles.redirectBannerLabel}>{redirectPending.animation_title}</div>
                                                            <a
                                                                className={styles.redirectBannerLink}
                                                                href={redirectPending.url}
                                                                target="_blank"
                                                                rel="noopener noreferrer"
                                                            >
                                                                {redirectPending.url}
                                                            </a>
                                                        </div>
                                                    </div>
                                                    <div className={styles.redirectBannerTrack}>
                                                        <div className={styles.redirectBannerBar} />
                                                    </div>
                                                </div>
                                            )}
                                            <div ref={chatMessageStreamEnd}></div>
                                        </div>
                                    )}

                                    {conversationLimitReached ? (
                                        <div
                                            style={{
                                                padding: "12px 16px",
                                                margin: "8px 0",
                                                backgroundColor: "#fef3cd",
                                                border: "1px solid #fde68a",
                                                fontSize: "13px",
                                                color: "#92400e",
                                                textAlign: "center",
                                                lineHeight: "1.5",
                                            }}
                                        >
                                            <span style={{ fontWeight: 600 }}>💬 Conversation limit reached</span>
                                            <br />
                                            {limitMessage}
                                        </div>
                                    ) : (
                                        <Stack horizontal className={styles.chatInput}>
                                            <button
                                                className={styles.attachButton}
                                                onClick={onPickFiles}
                                                aria-label="Attach images"
                                                title="Attach images"
                                                type="button"
                                            >
                                                <svg
                                                    xmlns="http://www.w3.org/2000/svg"
                                                    viewBox="0 0 16 16"
                                                    className={styles.attachIcon}
                                                    aria-hidden="true"
                                                >
                                                    <path
                                                        fill="none"
                                                        stroke="currentColor"
                                                        strokeLinecap="round"
                                                        d="M9.5 4v7.7c0 .8-.7 1.5-1.5 1.5s-1.5-.7-1.5-1.5V3C6.5 1.6 7.6.5 9 .5s2.5 1.1 2.5 2.5v9c0 1.9-1.6 3.5-3.5 3.5S4.5 13.9 4.5 12V4"
                                                    />
                                                </svg>
                                            </button>

                                            <QuestionInput
                                                clearOnSend
                                                placeholder="Ask something"
                                                disabled={isLoading || isError}
                                                onSend={(q) => sendQuestionRequest(q)}
                                                allowEmpty={isHelpMode && attachments.length > 0}
                                            />

                                            <input
                                                ref={fileInputRef}
                                                type="file"
                                                accept="image/*"
                                                multiple
                                                style={{ display: "none" }}
                                                onChange={onFilesSelected}
                                            />
                                        </Stack>
                                    )}

                                    {attachments.length > 0 && (
                                        <div className={styles.attachChipsRow}>
                                            {attachments.map((f, idx) => (
                                                <div key={`${f.name}-${idx}`} className={styles.attachChip} title={f.name}>
                                                    <img className={styles.attachThumb} src={URL.createObjectURL(f)} alt={f.name} />
                                                    <span className={styles.attachName}>{f.name}</span>
                                                    <button className={styles.attachRemove} onClick={() => removeAttachmentAt(idx)}>
                                                        ×
                                                    </button>
                                                </div>
                                            ))}
                                        </div>
                                    )}

                                    <Stack horizontal>
                                        <div
                                            style={{
                                                textAlign: "center",
                                                fontSize: "12px",
                                                color: "#191D28",
                                            }}
                                        >
                                            {isPluginType ? (
                                                <a
                                                    href="https://kust.ai"
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                    style={{
                                                        color: "#191D28",
                                                        fontWeight: "bold",
                                                        textDecoration: "none",
                                                        cursor: "pointer",
                                                    }}
                                                >
                                                    About this widget
                                                </a>
                                            ) : (
                                                <>
                                                    Powered by{" "}
                                                    <a
                                                        href="https://kust.ai"
                                                        target="_blank"
                                                        rel="noopener noreferrer"
                                                        style={{
                                                            color: "#191D28",
                                                            fontWeight: "bold",
                                                            textDecoration: "none",
                                                        }}
                                                    >
                                                        Kust
                                                        <img
                                                            src={Kust_Black}
                                                            className={styles.chatIcon}
                                                            style={{ width: "14px", height: "14px", verticalAlign: "middle", marginLeft: "4px" }}
                                                            aria-hidden="true"
                                                        />
                                                    </a>
                                                </>
                                            )}
                                        </div>
                                    </Stack>
                                </div>

                                {answers?.length > 0 && isCitationPanelOpen && activeCitation && (
                                    <Stack.Item className={styles.citationPanel} tabIndex={0} role="tabpanel" aria-label="Citations Panel">
                                        <Stack
                                            horizontal
                                            className={styles.citationPanelHeaderContainer}
                                            horizontalAlign="space-between"
                                            verticalAlign="center"
                                        >
                                            <span className={styles.citationPanelHeader}>Citations</span>
                                            <DismissRegular className={styles.citationPanelDismiss} onClick={() => setIsCitationPanelOpen(false)} />
                                        </Stack>
                                        <h5 className={styles.citationPanelTitle} tabIndex={0}>
                                            {activeCitation[2]}
                                        </h5>
                                        <div tabIndex={0}>
                                            <ReactMarkdown
                                                linkTarget="_blank"
                                                className={styles.citationPanelContent}
                                                children={activeCitation[0]}
                                                remarkPlugins={[remarkGfm]}
                                                rehypePlugins={[rehypeRaw]}
                                            />
                                        </div>
                                    </Stack.Item>
                                )}
                            </Stack>
                        </div>
                    )}
                </div>
            </HelpProvider>
        </Layout>
    ) : (
        <div></div>
    );
};

export default ChatWidget;

